
/*
 *   File : create-user.js
 *   Author URI : https://evoqins.com
 *   Description : Modal for creating user account
 *   Integrations : null
 *   Version : v1.1
 */

/* import packages */
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

/* import components */
import { GradientButton, SecondaryButton } from "../Cta";
import { Icon } from "../Icon";
import { CustomTextInput, RadioGroup, CustomSearchSelectBox, OTPInput, CustomPhoneInput } from "../FormElements";

//  service
import APIService from "../../Services/api-service";


// import style
import style from '../../Styles/Components/create-user.module.scss';
import { useSelector } from "react-redux";
import { _getZone } from "../../Helper/api";

const RELATION = [
    { value: 1, label: 'Self' },
    { value: 2, label: 'Father' }
]

export default function CreateUserAccount(props) {
    const navigator = useNavigate();
    const ADMIN_PROFILE_DATA = useSelector(state => state.MT_ADMIN_STORE.ADMIN_PROFILE);
    const adminTypeId = ADMIN_PROFILE_DATA?.admin_type_id;
    const adminId = ADMIN_PROFILE_DATA?.admin_id;

    const showZone = [1, 2, 3].includes(adminTypeId);
    const showRegion = adminTypeId <= 4;
    const showBranch = adminTypeId <= 5;
    const showRM = adminTypeId <= 6;
    const showAP = adminTypeId <= 7;

    const hasHierarchyMappingFields = showZone || showRegion || showBranch || showRM || showAP;
    const _mappingRowClass = (hasPreviousField) => `col-12${hasPreviousField ? " mt-4 pt-3" : ""}`;


    const [userNameType, setUserNameType] = useState(2);
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [mobile, setMobile] = useState("");
    const [mobileError, setMobileError] = useState("");
    const [relation, setRelation] = useState();
    const [relationOption, setRelationOption] = useState(RELATION);
    const [relationError, setRelationError] = useState("");
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [pin, setPin] = useState("");
    const [pinError, setPinError] = useState("");
    const [verificationScreen, setVerificationScreen] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [seconds, setSeconds] = useState(30);

    // zone
    const [zoneOptions, setZoneOptions] = useState([]);
    const [selectedZone, setSelectedZone] = useState({});

    const [selectedZoneError, setSelectedZoneError] = useState("");

    // region
    const [regionOptions, setRegionOptions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState({});
    const [selectedRegionError, setSelectedRegionError] = useState("");

    // manager
    const [managerOptions, setManagerOptions] = useState([]);
    const [selectedManager, setSelectedManager] = useState({});
    const [selectedManagerError, setSelectedManagerError] = useState("");

    // branch
    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState({});
    const [selectedBranchError, setSelectedBranchError] = useState("");

    // ap
    // const [apError, setApError] = useState("");
    const [ap, setAp] = useState([]);
    const [associateList, setAssociateList] = useState([]);



    useEffect(() => {
        var my_modal = document.getElementById("create-user");
        my_modal.addEventListener("hidden.bs.modal", () => {
            props.close();
        });
        //eslint-disable-next-line
    }, [props.close]);

    useEffect(() => {
        _getRelations();
    }, []);

    useEffect(() => {
        // Wait until profile is available
        if (!adminTypeId || !adminId) return;

        // reset cascade state whenever logged-in admin changes
        setSelectedZone({});
        setSelectedRegion({});
        setSelectedBranch({});
        setSelectedManager({});
        setAp("");
        setZoneOptions([]);
        setRegionOptions([]);
        setBranchOptions([]);
        setManagerOptions([]);
        setAssociateList([]);

        // Prefetch the first visible list, scoped by logged-in admin
        if ([1, 2, 3].includes(adminTypeId)) {
            _fetchZoneList();
            return;
        }

        if (adminTypeId === 4) {
            _fetchAndSetOptions("client_mapping/region/list", { id: adminId }, setRegionOptions);
            return;
        }

        if (adminTypeId === 5) {
            _fetchAndSetOptions("client_mapping/branch/list", { id: adminId }, setBranchOptions);
            return;
        }

        if (adminTypeId === 6) {
            _fetchAndSetOptions("client_mapping/rm/list", { id: adminId }, setManagerOptions);
            return;
        }

        if (adminTypeId === 7) {
            _getAssociateList(adminId);
        }
        // type 8: no dropdowns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminTypeId, adminId]);

    useEffect(() => {
        const countdown = setInterval(() => {
            if (seconds > 0) {
                setSeconds(seconds - 1);
            } else {
                clearInterval(countdown);
                // Handle expiration logic here (e.g., show a resend button)
            }
        }, 1000);

        return () => {
            clearInterval(countdown);
        };
    }, [seconds]);



    // handle radio 
    function _handleRadioInput(e) {
        setUserNameType(e);
    };

    // selected zone dropdown handler
    function _selectZone(zone) {
        setSelectedZone(zone);
        setSelectedRegion({});
        setSelectedBranch({});
        setSelectedManager({});
        setAp("");
        setRegionOptions([]);
        setBranchOptions([]);
        setManagerOptions([]);
        setAssociateList([]);
        setSelectedZoneError('');
        if (showRegion) {
            _fetchAndSetOptions("client_mapping/region/list", { id: zone.value }, setRegionOptions);
        }
    };

    // handle function region dropdown
    function _selectRegion(region) {
        setSelectedRegion(region);
        setSelectedBranch({});
        setSelectedManager({});
        setAp("");
        setBranchOptions([]);
        setManagerOptions([]);
        setAssociateList([]);
        setSelectedRegionError('');
        if (showBranch) {
            _fetchAndSetOptions("client_mapping/branch/list", { id: region.value }, setBranchOptions);
        }
    };

    // API - get Zone List
    const _getAssociateList = (rmId) => {
        let url = "client_mapping/ap/list";
        let body = { id: rmId };
        APIService(true, url, body).then(async (response) => {
            if (response.status_code === 200) {

                let ap_list = response.data.map((ap) => {
                    return {
                        ...ap,
                        label: ap.name,
                        value: ap.id
                    }
                });

                setAssociateList(ap_list);
                setAp("");
                // if (ap_list.length > 0) {
                // 	setAp(ap_list[0]);
                // }
            } else {
                setAssociateList([]);
                setAp("")
                toast.dismiss();
                toast.error(response);
            }
        });
    };

    // handle function branch dropdown
    function _selectBranch(branch) {
        setSelectedBranch(branch);
        setSelectedManager({});
        setAp("");
        setManagerOptions([]);
        setAssociateList([]);
        setSelectedBranchError('');
        if (showRM) {
            _fetchAndSetOptions("client_mapping/rm/list", { id: branch.value }, setManagerOptions);
        }
    }

    // handle name input change
    function _handleNameInput(e) {
        setName(e);
        setNameError("");
    }

    // handle email change
    function _handleEmailInput(e) {
        setEmail(e);
        setEmailError("");
    }

    // handle mobile change
    function _handleMobileInput(e) {
        setMobile(e);
        setMobileError("");
    }

    // handle relation dropdown
    function _handleRelation(e) {
        setRelation(e);
        setRelationError("");
    }

    // handle password
    function _handlePassword(e) {
        setPassword(e);
        setPasswordError("");
    }

    // handle pin 
    function _handlePin(e) {
        setPin(e);
        setPinError("");
    }

    // handle otp
    function _handleOTP(e) {
        setOtp(e);
        setOtpError('');
    }

    // Form validation
    function _validateForm() {
        console.log('ruwi')
        let valid = true;
        if (name === "") {
            setNameError("Name is required");
            valid = false;
        }
        if (userNameType === 2) {
            if (email === "") {
                setEmailError('Email is required');
                valid = false;
            }
            else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
                setEmailError('Enter valid Email');
                valid = false;
            }
        }
        if (userNameType === 1) {
            if (mobile === "") {
                setMobileError('Mobile is required');
                valid = false;
            }
            else if (!/^[6-9]\d*$/.test(mobile)) {
                valid = false;
                setMobileError("Enter valid Mobile");
            }
        }

        if (relation === null) {
            setRelationError('Relation is required');
            valid = false;
        }

        if (password.length < 4) {
            setPasswordError('Enter valid password');
            valid = false;
        }
        if (pin === "") {
            setPinError('Pin is required');
            valid = false;
        }

        if (valid === true) {
            _signUp();
        }
    }

    // API - get signup relation
    function _getRelations() {
        let url = 'user/signup-relation';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                let relation_data = response.data.map((item) => {
                    return ({ value: item.id, label: item.name })
                });
                setRelationOption(relation_data);
                setRelation(relation_data[0]);
            }
        })
    };

    // API- helper function to fetch and set data for region, branch and manager
    async function _fetchAndSetOptions(url, request_data, setOptionsCallback) {
        try {
            const response = await APIService(true, url, request_data);

            if (response.status_code === 200) {
                const mappedData = response.data.map((item) => ({
                    value: item.id,
                    label: item.name,
                    code: item.code
                }));
                setOptionsCallback(mappedData);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                });
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error, {
                type: "error",
            });
        }
    };

    // API - get zone list
    async function _fetchZoneList() {
        let data = await _getZone();
        const modified_data = data.map((item) => {
            return {
                value: item.id,
                label: item.name
            }
        })
        setZoneOptions(modified_data);
    };

    // (Legacy) _getManagerList removed: use scoped prefetch effect above.

    // API - signup user
    function _signUp() {
        setButtonLoader(true);
        let url = 'user/create-customer';
        const scopedZoneId = adminTypeId === 4 ? adminId : undefined;
        const scopedRegionId = adminTypeId === 5 ? adminId : undefined;
        const scopedBranchId = adminTypeId === 6 ? adminId : undefined;
        const scopedRmId = adminTypeId === 7 ? adminId : undefined;

        let payload = {
            type: userNameType,
            zone_id: showZone ? selectedZone?.value : scopedZoneId,
            region_id: showRegion ? selectedRegion?.value : scopedRegionId,
            branch_id: showBranch ? selectedBranch?.value : scopedBranchId,
            rm_id: showRM ? selectedManager?.value : scopedRmId,
            ap_id: ap?.value || null,
            name,
            username: userNameType === 1 ? mobile : email,
            relation: relation.value,
            password,
            pin
        };

        if (adminTypeId == 8) {
            delete payload.ap_id;
        }

        payload = JSON.stringify(payload);

        let payloadObj = payload;

        APIService(true, url, payloadObj).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success(`OTP sent to ${userNameType === 1 ? mobile : email}`, {
                    type: 'success'
                });
                setVerificationScreen(true);
                setSeconds(29);
            } else {
                setOtp("");
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setButtonLoader(false);
        })
    };

    // API - resend OTP 
    function _handleResend() {
        let url = 'user/resend-otp';
        let payload = JSON.stringify({
            username: userNameType === 1 ? mobile : email,
            country_code: null,
            relation: relation.value
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("OTP sent successfully", {
                    type: "success",
                });
                setOtp("");
                setSeconds(30);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error"
                });
            }
        })
    };

    // handle function rm dropdown
    function _selectManager(rm) {
        setSelectedManager(rm);
        setSelectedManagerError('');
        setAp("");
        setAssociateList([]);
        if (showAP) {
            _getAssociateList(rm.value);
        }

    };

    const _handleAPSelect = (value) => {
        setAp(value);
        // setApError('')
    };

    // API - verify OTP
    function _verifyPin() {
        if (otp === "") {
            setOtpError('OPT is required');
            return;
        }
        setButtonLoader(true);
        let url = 'user/verify-otp';
        let payload = JSON.stringify({
            otp,
            type: "SIGNUP",
            username: userNameType === 1 ? mobile : email,
            relation: relation.value
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success('Successfully created the user', {
                    type: 'success'
                });
                const close_button = document.getElementById("close-modal");
                close_button.click();
                navigator('/create-account', {
                    state: {
                        user_id: response.data
                    }
                });
            } else {
                setOtp("");
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setButtonLoader(false);
        })
    };

    return (
        <div id="create-user"
            // data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="active-sip"
            aria-hidden="true"
            className={`modal fade ${style.e_create_user}`}>
            <div className={`modal-dialog modal-dialog-scrollable position-relative modal-dialog-centered ${style.e_dialog}`}>
                <div className={`modal-content  bg-white position-relative text-center d-flex align-items-center p-4  ${style.e_content} `}>
                    <button className="d-none" data-bs-dismiss="modal" id="close-modal"></button>
                    <div className="col-12 d-flex justify-content-between">
                        <p className="color-jett-black e-poppins-semi-bold e-font-18 mb-0">Create user</p>
                        <Icon icon="close"
                            size={24}
                            className="cursor-pointer ms-auto e-modal-close-btn"
                            data-bs-dismiss="modal" />
                    </div>

                    {
                        verificationScreen === false ?
                            <div className={`row ${style.content_wrapper} modal-body`} id="modal-body">
                                <div className="margin-16px-top d-lg-flex d-md-flex d-block gap-20px">
                                    <p className="color-black e-poppins-regular e-font-16 line-height-16px mb-2 mt-2">Create account with</p>
                                    <div className="d-flex align-items-center gap-8px cursor-pointer"
                                        onClick={() => _handleRadioInput(2)}>
                                        <Icon icon={userNameType === 2 ? "radio-selected" : "radio-unselected"}
                                            size={20} />
                                        <p className="color-outer-space e-poppins-regular e-font-14 line-height-24px mb-0">Mail</p>
                                    </div>
                                    <div className="d-flex align-items-center gap-8px cursor-pointer mt-lg-0 mt-md-0 mt-3"
                                        onClick={() => _handleRadioInput(1)}>
                                        <Icon icon={userNameType === 1 ? "radio-selected" : "radio-unselected"}
                                            size={20} />
                                        <p className="color-outer-space e-poppins-regular e-font-14 line-height-24px mb-0">Mobile</p>
                                    </div>

                                </div>
                                <div className="col-12 mt-3">
                                    <CustomTextInput label="Name"
                                        postfix="*"
                                        personName={true}
                                        id={`name-input`}
                                        value={name}
                                        error={nameError}
                                        onEnter={_validateForm}
                                        valueChange={_handleNameInput} />
                                </div>
                                <div className="col-12 mt-4 pt-2">
                                    {
                                        userNameType === 2 ? (
                                            <CustomTextInput label="Email"
                                                postfix="*"
                                                id={`email-input`}
                                                value={email}
                                                error={emailError}
                                                onEnter={_validateForm}
                                                valueChange={_handleEmailInput} />
                                        ) : (
                                            <CustomPhoneInput label="Phone"
                                                value={mobile}
                                                postfix="*"
                                                id='number-input'
                                                type="number"
                                                error={mobileError}
                                                handleChange={_handleMobileInput} />
                                        )
                                    }
                                </div>
                                <div className="col-12 text-start">
                                    <CustomSearchSelectBox value={relation ? relation : ""}
                                        label="Relation"
                                        error={relationError}
                                        postfix="*"
                                        className={`${hasHierarchyMappingFields ? "margin-32px-bottom" : ""} margin-32px-top`}
                                        options={relationOption}
                                        // onMenuToggle={_handleDropdownOpen}
                                        onSelectChange={_handleRelation} />
                                </div>


                                {/* Hierarchy mapping (scoped by logged-in admin type) */}
                                {hasHierarchyMappingFields && (
                                    <>
                                        {showZone && (
                                            <div className={_mappingRowClass(false)}>
                                                <CustomSearchSelectBox options={zoneOptions}
                                                    value={Object.keys(selectedZone).length === 0 ? "" : selectedZone}
                                                    label="Select region"
                                                    error={selectedZoneError}
                                                    id="admin-zone"
                                                    hide_error_text={false}
                                                    placeholder=""
                                                    postfix="*"
                                                    onSelectChange={_selectZone} />
                                            </div>
                                        )}

                                        {showRegion && (
                                            <div className={_mappingRowClass(showZone)}>
                                                <CustomSearchSelectBox options={regionOptions}
                                                    value={Object.keys(selectedRegion).length === 0 ? "" : selectedRegion}
                                                    label="Select Zone"
                                                    error={selectedRegionError}
                                                    isDisabled={showZone ? (selectedZone?.value ? false : true) : false}
                                                    hide_error_text={false}
                                                    id="admin-region-input"
                                                    placeholder=""
                                                    postfix="*"
                                                    onSelectChange={_selectRegion} />
                                            </div>
                                        )}

                                        {showBranch && (
                                            <div className={_mappingRowClass(showZone || showRegion)}>
                                                <CustomSearchSelectBox options={branchOptions}
                                                    value={Object.keys(selectedBranch).length === 0 ? "" : selectedBranch}
                                                    label="Select Branch"
                                                    error={selectedBranchError}
                                                    isDisabled={showRegion ? (selectedRegion?.value ? false : true) : false}
                                                    id="admin-branch-input"
                                                    hide_error_text={false}
                                                    placeholder=""
                                                    postfix="*"
                                                    onSelectChange={_selectBranch} />
                                            </div>
                                        )}

                                        {showRM && (
                                            <div className={_mappingRowClass(showZone || showRegion || showBranch)}>
                                                <CustomSearchSelectBox options={managerOptions}
                                                    value={Object.keys(selectedManager).length === 0 ? "" : selectedManager}
                                                    label="RM"
                                                    error={selectedManagerError}
                                                    isDisabled={showBranch ? (selectedBranch?.value ? false : true) : false}
                                                    id="admin-manager-input"
                                                    hide_error_text={false}
                                                    placeholder=""
                                                    postfix="*"
                                                    onSelectChange={_selectManager} />
                                            </div>
                                        )}

                                        {showAP && (
                                            <div className={_mappingRowClass(showZone || showRegion || showBranch || showRM)}>
                                                <CustomSearchSelectBox options={associateList}
                                                    value={ap}
                                                    isDisabled={showRM ? (selectedManager?.value ? false : true) : false}
                                                    id="ap-dropdown"
                                                    hide_error_text={false}
                                                    placeholder="AP"
                                                    onSelectChange={_handleAPSelect} />
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="col-12  mt-4 pt-3">
                                    <CustomTextInput label="Password"
                                        postfix="*"
                                        type={showPassword ? "text" : "password"}
                                        // hide_error_text={true}
                                        value={password}
                                        error={passwordError}
                                        onEnter={_validateForm}
                                        password_icon={true}
                                        isAuthInput={true}
                                        showPassword={() => {
                                            setShowPassword(!showPassword)
                                        }}
                                        valueChange={_handlePassword} />
                                </div>
                                <div className="col-12 mt-3 pt-3">
                                    <CustomTextInput label="Pin"
                                        postfix="*"
                                        type={'number'}
                                        max={4}
                                        value={pin}
                                        error={pinError}
                                        onEnter={_validateForm}
                                        valueChange={_handlePin} />
                                    {/* <OTPInput pinString={pin}
                                id="sip-otp"
                                autoFocus={true}
                                pinError={pinError}
                                handleChange={_handlePin} /> */}
                                </div>
                                <div className="col-12">
                                    <SecondaryButton label="Cancel"
                                        cancel="modal" />
                                    <GradientButton label="Create account"
                                        loading={buttonLoader}
                                        className=" padding-14px-tb ms-3 margin-32px-top"
                                        onPress={_validateForm} />
                                </div>
                            </div>
                            :
                            <div className={`row ${style.content_wrapper} mt-5`} id="modal-body">
                                <p className="color-outer-space e-poppins-regular e-font-16 mb-4 margin-20px-top">
                                    {`Enter OTP received in ${userNameType === 2 ? email : `+91 ${mobile}`}`}
                                </p>
                                <div className="col-12">
                                    <img draggable={false} src={require("../../Assets/Images/Modal/pin_illustration.png")}
                                        alt="Kyc success"
                                        width={72}
                                        height={72}
                                        className="mx-auto mb-5 mt-3" />
                                </div>
                                <div className="col-12">
                                    <OTPInput pinString={otp}
                                        id="sip-otp"
                                        autoFocus={true}
                                        pinError={otpError}
                                        handleChange={_handleOTP} />
                                </div>
                                <div className="col-8 offset-2">
                                    <GradientButton label="Submit"
                                        loading={buttonLoader}
                                        className=" padding-14px-tb margin-32px-top"
                                        onPress={_verifyPin} />
                                </div>
                                <div className="d-flex justify-content-center mt-4">
                                    {
                                        seconds === 0 ?
                                            <p className='color-primary-color e-poppins-regular e-font-14 line-height-14px cursor-pointer mb-0 e-forgot-pin position-relative w-max-content resend'
                                                onClick={_handleResend}>Resend OTP</p>
                                            :
                                            <p className='color-primary-color e-poppins-regular e-font-14 line-height-14px cursor-not-allowed mb-0 '>
                                                Resend OTP in <span className="color-black">{seconds}s</span>
                                            </p>
                                    }

                                </div>
                            </div>

                    }

                </div>
            </div>
        </div>
    )
} 
